import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Kept for call-site compatibility; width is fixed by theme (1140px / fullscreen). */
  size?: 'sm' | 'lg' | 'xl';
};

/**
 * React-controlled modal (Bootstrap CSS only).
 * Desktop: 1140px wide. Mobile: fullscreen.
 */
export function AppModal({ open, title, onClose, children, footer }: Props) {
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
      document.body.style.overflow = prevOverflow;
      document.querySelectorAll('body > .modal-backdrop').forEach((el) => el.remove());
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="modal-backdrop fade show" onClick={() => onCloseRef.current()} />
      <div
        className="modal fade show d-block app-modal"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-dialog-scrollable app-modal__dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content app-modal__content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold" id={titleId}>
                {title}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => onCloseRef.current()}
              />
            </div>
            <div className="modal-body">{children}</div>
            {footer ? <div className="modal-footer">{footer}</div> : null}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
